import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type { PlayoffOdds, StandingRow } from "@averagejoesff/shared";
import { formatOdds, formatOrdinal, formatPoints, formatRecord } from "../../lib/format";
import type { PeopleLookup } from "../managers/people";
import { ManagerLink } from "../managers/people";

interface StandingsTableProps {
  standings: readonly StandingRow[];
  people: PeopleLookup;
  /** Highlighted as the visitor's own row. */
  myId: string | null;
  caption: string;
  /** The playoff forecast, or null where there isn't one (a finished season, or no schedule recorded). */
  odds?: PlayoffOdds | null;
  /** How many teams make the playoffs; marks the cutoff in the default order. Null when unknown. */
  playoffTeams?: number | null;
}

type SortKey = "rank" | "record" | "pointsFor" | "pointsAgainst" | "playoffs" | "title";
type SortDirection = "asc" | "desc";
interface Sort {
  key: SortKey;
  direction: SortDirection;
}

/** Yahoo's order, best first; every other column starts with its biggest number on top. */
const DEFAULT_SORT: Sort = { key: "rank", direction: "asc" };

/** On a phone only one of these column sets fits beside Record; from 600px both show. */
type ColumnSet = "odds" | "points";
const SORT_KEYS_OF: Record<ColumnSet, readonly SortKey[]> = { odds: ["playoffs", "title"], points: ["pointsFor", "pointsAgainst"] };
const SORT_LABELS: Record<SortKey, string> = {
  rank: "rank",
  record: "record",
  pointsFor: "points for",
  pointsAgainst: "points against",
  playoffs: "playoff odds",
  title: "title odds",
};

const winShare = (row: StandingRow) => {
  const { wins, losses, ties } = row.record;
  const games = wins + losses + ties;
  return games === 0 ? 0 : (wins + ties / 2) / games;
};

export function StandingsTable({ standings, people, myId, caption, odds = null, playoffTeams = null }: StandingsTableProps) {
  const oddsByPerson = useMemo(() => new Map(odds?.teams.map((t) => [t.personId, t])), [odds]);
  const settled = odds?.weeksRemaining === 0;
  const showTitle = odds?.playoffTeams === 8;

  const [sort, setSort] = useState<Sort>(DEFAULT_SORT);
  const [columnSet, setColumnSet] = useState<ColumnSet>(odds ? "odds" : "points");
  // Without a forecast there is nothing to switch to; the points columns always show.
  const activeSet: ColumnSet = odds ? columnSet : "points";

  const rows = useMemo(() => {
    const value = (row: StandingRow): number => {
      switch (sort.key) {
        case "rank":
          return row.rank;
        case "record":
          return winShare(row);
        case "pointsFor":
          return row.pointsFor;
        case "pointsAgainst":
          return row.pointsAgainst;
        case "playoffs":
          return oddsByPerson.get(row.personId)?.playoffOdds ?? 0;
        case "title":
          return oddsByPerson.get(row.personId)?.titleOdds ?? 0;
      }
    };
    const sign = sort.direction === "asc" ? 1 : -1;
    // Ties fall back to the official order, so equal numbers never shuffle.
    const sorted = [...standings].sort((a, b) => sign * (value(a) - value(b)) || a.rank - b.rank);
    // The # column counts down the rows as sorted, so "7th in points" reads straight off the table.
    // Equal numbers share a place; the default order keeps Yahoo's official rank, which also breaks ties.
    let place = 0;
    return sorted.map((row, index) => {
      if (sort.key === "rank") return { row, place: row.rank };
      if (index === 0 || value(row) !== value(sorted[index - 1]!)) place = index + 1;
      return { row, place };
    });
  }, [standings, sort, oddsByPerson]);

  const inDefaultOrder = sort.key === DEFAULT_SORT.key && sort.direction === DEFAULT_SORT.direction;
  const cutoffAfter = inDefaultOrder && playoffTeams !== null && playoffTeams > 0 && playoffTeams < rows.length ? playoffTeams : null;

  /** Switching which pair of columns shows must not leave the rows ordered by a column that just disappeared. */
  function chooseColumns(next: ColumnSet) {
    setColumnSet(next);
    setSort((current) => (SORT_KEYS_OF[next === "odds" ? "points" : "odds"].includes(current.key) ? DEFAULT_SORT : current));
  }

  function sortBy(key: SortKey) {
    setSort((current) => {
      if (key === "rank") return DEFAULT_SORT;
      // First click: biggest first. Second: smallest first.
      return current.key === key && current.direction === "desc" ? { key, direction: "asc" } : { key, direction: "desc" };
    });
  }

  // On a phone the other column set is hidden, so :last-child can't find the column against the card's edge.
  const edgeKey: SortKey = activeSet === "points" ? "pointsAgainst" : showTitle ? "title" : "playoffs";
  const edge = (key: SortKey) => (key === edgeKey ? "standings__edge" : "");

  const header = (key: SortKey, label: string, options: { className?: string; hiddenSuffix?: string; numeric?: boolean } = {}) => {
    const active = sort.key === key;
    const Icon = !active ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;
    return (
      <th
        scope="col"
        className={[options.numeric === false ? "" : "standings__num", options.className, edge(key)].filter(Boolean).join(" ")}
        aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
      >
        <button type="button" className="standings__sort" onClick={() => sortBy(key)}>
          <span>
            {label}
            {options.hiddenSuffix && <span className="visually-hidden">{options.hiddenSuffix}</span>}
          </span>
          <Icon className={active ? "standings__sort-icon standings__sort-icon--on" : "standings__sort-icon"} aria-hidden="true" size={12} />
        </button>
      </th>
    );
  };

  return (
    <>
      {odds && (
        <div className="standings__views" role="group" aria-label="Columns to show">
          <button type="button" className="standings__view" aria-pressed={columnSet === "odds"} onClick={() => chooseColumns("odds")}>
            Playoff odds
          </button>
          <button type="button" className="standings__view" aria-pressed={columnSet === "points"} onClick={() => chooseColumns("points")}>
            Points
          </button>
        </div>
      )}
      {!inDefaultOrder && (
        <p className="standings__sorted">
          Sorted by {SORT_LABELS[sort.key]}, {sort.direction === "desc" ? "highest first" : "lowest first"} ·{" "}
          <button type="button" className="standings__reset" onClick={() => setSort(DEFAULT_SORT)}>
            Back to standings order
          </button>
        </p>
      )}
      <div className="table-scroll">
        <table className="standings" data-columns={activeSet}>
          <caption className="visually-hidden">{caption}</caption>
          <thead>
            <tr>
              {header("rank", "#", { numeric: false, hiddenSuffix: " Rank", className: "standings__rank" })}
              <th scope="col">Team</th>
              {header("record", "Record")}
              {header("pointsFor", "PF", { className: "standings__col--points", hiddenSuffix: " points for" })}
              {header("pointsAgainst", "PA", { className: "standings__col--points", hiddenSuffix: " points against" })}
              {odds && header("playoffs", "Playoffs", { className: "standings__col--odds", hiddenSuffix: " chance" })}
              {odds && showTitle && header("title", "Title", { className: "standings__col--odds", hiddenSuffix: " chance" })}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ row, place }, index) => {
              const chances = oddsByPerson.get(row.personId);
              const classes = ["standings__row"];
              if (row.personId === myId) classes.push("standings__row--me");
              if (cutoffAfter === index + 1) classes.push("standings__row--cutoff");
              return (
                <tr key={row.personId} className={classes.join(" ")}>
                  <td className="standings__rank" aria-label={formatOrdinal(place)}>
                    {place}
                  </td>
                  <td className="standings__team">
                    <span className="standings__team-name">{row.teamName}</span>
                    <ManagerLink personId={row.personId} people={people} />
                  </td>
                  <td className="standings__num">{formatRecord(row.record.wins, row.record.losses, row.record.ties)}</td>
                  <td className="standings__num standings__col--points">{formatPoints(row.pointsFor)}</td>
                  <td className={`standings__num standings__col--points ${edge("pointsAgainst")}`}>{formatPoints(row.pointsAgainst)}</td>
                  {odds && <td className={`standings__num standings__odds standings__col--odds ${edge("playoffs")}`}>{formatOdds(chances?.playoffOdds ?? 0, settled)}</td>}
                  {odds && showTitle && (
                    <td className={`standings__num standings__odds standings__col--odds ${edge("title")}`}>
                      {/* No title odds for a team that's out; "<1%" would suggest a chance. */}
                      {chances?.playoffOdds === 0 ? "—" : formatOdds(chances?.titleOdds ?? 0)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {cutoffAfter !== null && <p className="standings__legend">The dashed line is the playoff cutoff: the top {cutoffAfter} advance.</p>}
    </>
  );
}
